<template>
	<div class="device-check">
		<b-alert :show="!!error" variant="danger" class="mb-2" style="max-width: 480px">{{error}}</b-alert>
		<div class="device-check-card">
			<h5 class="text-center mb-3">Let's check your camera and mic</h5>
			<div class="preview-wrap">
				<video ref="previewVideo" muted autoplay playsinline :class="['preview-video', { 'preview-video-off': camMuted }]"></video>
				<div v-if="needsAutoplayUnlock || !previewActive || camMuted" :class="['preview-off', { 'preview-off-clickable': needsAutoplayUnlock }]" @click="unlockAutoplay">
					<template v-if="needsAutoplayUnlock">
						<b-icon icon="play-circle-fill" font-scale="3" variant="primary"></b-icon>
						<small class="text-muted mt-2 px-3 text-center" style="font-size:.75rem">Click to enable camera preview</small>
					</template>
					<template v-else-if="previewWaiting && !previewActive">
						<b-spinner variant="secondary"></b-spinner>
						<small class="text-muted mt-2 px-3 text-center" style="font-size:.75rem">Waiting for camera access&hellip;<br>Check the browser permission bar.</small>
					</template>
					<template v-else>
						<b-icon icon="camera-video-off-fill" font-scale="3" variant="secondary"></b-icon>
						<small class="text-muted mt-2" style="font-size:.75rem">Camera Disabled</small>
					</template>
				</div>
				<div class="mic-bar-wrap" :title="`Mic level: ${Math.round(audioLevel)}`">
					<b-icon :icon="micMuted ? 'mic-mute-fill' : 'mic-fill'" :class="['mic-icon', { 'text-danger': micMuted }]"></b-icon>
					<div class="mic-bar">
						<div class="mic-fill" :style="{ width: micMuted ? '0%' : (Math.min(100, audioLevel * 1500) + '%') }"></div>
					</div>
				</div>
			</div>

			<div v-if="showNameField" class="mt-3">
				<b-form-input v-model="name" placeholder="Your name" required autofocus @input="$emit('update:name', name)"></b-form-input>
			</div>

			<div class="ctrl-row">
				<b-dropdown split dropup :variant="micMuted ? 'danger' : 'secondary'" @click="toggleMic" v-b-tooltip.top :title="micMuted ? 'Unmute mic' : 'Mute mic'">
					<template #button-content><b-icon :icon="micMuted ? 'mic-mute-fill' : 'mic-fill'"></b-icon></template>
					<b-dropdown-item v-for="opt in micOptions" :key="opt.value || 'default-mic'" :active="selectedMic === opt.value" @click="selectedMic = opt.value; onDeviceChange()">{{opt.text}}</b-dropdown-item>
				</b-dropdown>
				<b-dropdown split dropup :variant="camMuted ? 'danger' : 'secondary'" :disabled="camPending" @click="toggleCam" v-b-tooltip.top :title="camMuted ? 'Start camera' : 'Stop camera'">
					<template #button-content>
						<b-spinner v-if="camPending" small></b-spinner>
						<b-icon v-else :icon="camMuted ? 'camera-video-off-fill' : 'camera-video-fill'"></b-icon>
					</template>
					<b-dropdown-item v-for="opt in cameraOptions" :key="opt.value || 'default-camera'" :active="selectedCamera === opt.value" @click="selectedCamera = opt.value; onDeviceChange()">{{opt.text}}</b-dropdown-item>
				</b-dropdown>
				<b-button variant="secondary" @click="showSettings = true" v-b-tooltip.top title="Settings">
					<b-icon icon="gear-fill"></b-icon>
				</b-button>
			</div>

			<b-button variant="primary" block class="mt-3" :disabled="busy || (showNameField && !name)" @click="confirm">
				<b-spinner v-if="busy" small class="mr-1"></b-spinner>
				{{confirmLabel}}
			</b-button>
			<p v-if="subtitle" class="text-muted text-center small mt-2 mb-0">{{subtitle}}</p>
		</div>

		<b-modal v-model="showSettings" title="Settings" hide-footer>
			<b-form-group label="Camera">
				<b-form-select v-model="selectedCamera" :options="cameraOptions" @change="onDeviceChange"></b-form-select>
			</b-form-group>
			<b-form-group label="Microphone">
				<b-form-select v-model="selectedMic" :options="micOptions" @change="onDeviceChange"></b-form-select>
			</b-form-group>
			<b-form-group label="Microphone processing">
				<b-form-checkbox v-model="audioSettings.echoCancellation" class="mb-1" @change="onDeviceChange">Echo cancellation</b-form-checkbox>
				<b-form-checkbox v-model="audioSettings.noiseSuppression" class="mb-1" @change="onDeviceChange">Noise suppression</b-form-checkbox>
				<b-form-checkbox v-model="audioSettings.autoGainControl" @change="onDeviceChange">Auto gain control</b-form-checkbox>
			</b-form-group>
		</b-modal>
	</div>
</template>

<script>
import { getUserMediaWithFallback, getUserMediaWithTimeout } from '@/callMedia';

export default {
	props: {
		confirmLabel: { type: String, default: 'Enter the studio' },
		subtitle: { type: String, default: null },
		showNameField: { type: Boolean, default: false },
		initialName: { type: String, default: '' },
	},
	data() { return {
		error: null,
		busy: false,
		devices: [],
		selectedCamera: null,
		selectedMic: null,
		previewStream: null,
		previewActive: false,
		previewWaiting: false,
		needsAutoplayUnlock: false,
		micMuted: false,
		// Off by default — a lot of people bounce off an immediate camera permission prompt.
		// startPreview() only requests audio until they explicitly opt in via toggleCam().
		camMuted: true,
		camPending: false,
		showSettings: false,
		audioSettings: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
		audioLevel: 0,
		audioCtx: null,
		audioTimer: null,
		name: this.initialName,
	}},
	computed: {
		// Firefox lists a device without an id until that kind of device has been granted; it can't be selected anyway.
		cameraOptions() {
			return [
				{ value: null, text: 'Default camera' },
				...this.devices.filter(d => d.kind === 'videoinput' && d.deviceId).map(d => ({ value: d.deviceId, text: d.label || `Camera ${d.deviceId.slice(0, 8)}` })),
			];
		},
		micOptions() {
			return [
				{ value: null, text: 'Default microphone' },
				...this.devices.filter(d => d.kind === 'audioinput' && d.deviceId).map(d => ({ value: d.deviceId, text: d.label || `Mic ${d.deviceId.slice(0, 8)}` })),
			];
		},
	},
	async mounted() {
		await this.startPreview();
	},
	beforeDestroy() {
		this._destroyed = true;
		// A confirmed check hands its stream to the parent - only torn down here if the
		// component is destroyed without ever confirming (e.g. the user navigated away).
		if(!this._handedOff) this.stopPreview();
	},
	methods: {
		audioConstraints() {
			return {
				...(this.selectedMic ? { deviceId: { exact: this.selectedMic } } : {}),
				echoCancellation: this.audioSettings.echoCancellation,
				noiseSuppression: this.audioSettings.noiseSuppression,
				autoGainControl: this.audioSettings.autoGainControl,
			};
		},
		async startPreview() {
			try {
				this.stopPreview();
				this.previewActive = false;
				this.previewWaiting = true;
				let stream;
				try {
					stream = await getUserMediaWithTimeout({
						video: this.camMuted ? false : (this.selectedCamera ? { deviceId: { exact: this.selectedCamera } } : true),
						audio: this.audioConstraints(),
					});
					this.previewActive = !this.camMuted;
				} catch(e) {
					console.error('Preview video+audio error', e);
					if(this.camMuted) {
						if(!this._destroyed) this.error = e.name === 'NotAllowedError' ? 'Microphone access denied. Please allow access in your browser, then reload.' : (e.message || 'Could not access microphone.');
						return;
					}
					try {
						stream = await getUserMediaWithTimeout({ video: false, audio: this.audioConstraints() });
					} catch(e2) {
						console.error('Preview audio-only error', e2);
						if(!this._destroyed) this.error = e.name === 'NotAllowedError' ? 'Camera/microphone access denied. Please allow access in your browser, then reload.' : (e.message || 'Could not access camera or microphone.');
						return;
					}
				}
				if(this._destroyed) { stream.getTracks().forEach(t => t.stop()); return; }
				this.previewStream = stream;
				this.setupAudioLevel(stream);
				navigator.mediaDevices.enumerateDevices().then(devices => { this.devices = devices; }).catch(() => {});
				await this.$nextTick();
				if(this.$refs.previewVideo) {
					this.$refs.previewVideo.srcObject = stream;
					try {
						await this.$refs.previewVideo.play();
						this.needsAutoplayUnlock = false;
					} catch(e) {
						if(e.name === 'NotAllowedError') this.needsAutoplayUnlock = true;
						else console.error('preview play error', e);
					}
				}
			} catch(e) {
				console.error('Preview error', e);
				this.previewActive = false;
			} finally {
				this.previewWaiting = false;
			}
		},
		stopPreview() {
			this.stopAudioLevel();
			this.previewStream?.getTracks().forEach(t => t.stop());
			this.previewStream = null;
			this.previewActive = false;
		},
		setupAudioLevel(stream) {
			this.stopAudioLevel();
			try {
				this.audioCtx = new AudioContext();
				const src = this.audioCtx.createMediaStreamSource(stream);
				const analyser = this.audioCtx.createAnalyser();
				analyser.fftSize = 1024;
				const silentGain = this.audioCtx.createGain();
				silentGain.gain.value = 0;
				src.connect(analyser);
				analyser.connect(silentGain);
				silentGain.connect(this.audioCtx.destination);
				const buf = new Float32Array(analyser.fftSize);
				this.audioCtx.resume().catch(() => {});
				const resume = () => {
					if(this.audioCtx?.state === 'suspended') this.audioCtx.resume().catch(e => console.error('AudioContext resume error', e));
					else {
						document.removeEventListener('click', resume);
						document.removeEventListener('keydown', resume);
						this._resumeHandler = null;
					}
				};
				this._resumeHandler = resume;
				document.addEventListener('click', resume);
				document.addEventListener('keydown', resume);
				this.audioTimer = setInterval(() => {
					analyser.getFloatTimeDomainData(buf);
					let sum = 0;
					for(let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
					this.audioLevel = Math.sqrt(sum / buf.length);
				}, 80);
			} catch(e) {
				console.error('Audio level setup error', e);
			}
		},
		stopAudioLevel() {
			clearInterval(this.audioTimer);
			this.audioTimer = null;
			if(this._resumeHandler) {
				document.removeEventListener('click', this._resumeHandler);
				document.removeEventListener('keydown', this._resumeHandler);
				this._resumeHandler = null;
			}
			try { this.audioCtx?.close(); } catch(e) { console.error('AudioContext close error', e); }
			this.audioCtx = null;
			this.audioLevel = 0;
		},
		unlockAutoplay() {
			if(!this.needsAutoplayUnlock) return;
			this.needsAutoplayUnlock = false;
			this.$refs.previewVideo?.play().catch(e => console.error('autoplay unlock error', e));
			this.audioCtx?.resume().catch(() => {});
		},
		updatePreviewTracks() {
			if(!this.previewStream) return;
			this.previewStream.getAudioTracks().forEach(t => { t.enabled = !this.micMuted; });
			this.previewStream.getVideoTracks().forEach(t => { t.enabled = !this.camMuted; });
		},
		toggleMic() {
			this.micMuted = !this.micMuted;
			this.updatePreviewTracks();
			this.audioCtx?.resume();
		},
		// The camera only ever gets asked for on this explicit click, not on mount - see startPreview().
		async toggleCam() {
			this.camMuted = !this.camMuted;
			if(this.camMuted || !this.previewStream || this.previewStream.getVideoTracks().length) {
				this.updatePreviewTracks();
				this.audioCtx?.resume();
				return;
			}
			this.camPending = true;
			try {
				this.previewStream?.getTracks().forEach(t => t.stop());
				this.previewStream = null;
				let combined;
				try {
					combined = await getUserMediaWithFallback({
						video: this.selectedCamera ? { deviceId: { exact: this.selectedCamera } } : true,
						audio: this.audioConstraints(),
					});
				} catch(e) {
					this.camMuted = true;
					this.error = e.name === 'NotAllowedError' ? 'Camera access denied. Please allow access in your browser.' : (e.message || 'Could not access camera.');
					await this.startPreview();
				}
				if(combined) {
					this.previewStream = combined;
					this.previewActive = true;
					this.setupAudioLevel(combined);
					navigator.mediaDevices.enumerateDevices().then(devices => { this.devices = devices; }).catch(() => {});
					await this.$nextTick();
					if(this.$refs.previewVideo) {
						this.$refs.previewVideo.srcObject = combined;
						await this.$refs.previewVideo.play().catch(e => console.error('preview play error', e));
					}
				}
			} finally {
				this.camPending = false;
			}
			this.updatePreviewTracks();
			this.audioCtx?.resume();
		},
		async onDeviceChange() {
			await this.restartPreview();
		},
		async restartPreview() {
			await this.startPreview();
			this.updatePreviewTracks();
		},
		confirm() {
			if(this.showNameField && !this.name) return;
			this.busy = true;
			// Stop the level-meter analysis only - the media tracks stay alive and pass to the parent.
			this.stopAudioLevel();
			this.previewActive = false;
			if(this.$refs.previewVideo) this.$refs.previewVideo.srcObject = null;
			this._handedOff = true;
			this.$emit('ready', {
				stream: this.previewStream,
				cameraOn: !this.camMuted,
				micOn: !this.micMuted,
				cameraDeviceId: this.selectedCamera,
				micDeviceId: this.selectedMic,
				audioSettings: { ...this.audioSettings },
				name: this.name || null,
			});
		},
	},
};
</script>

<style lang="scss" scoped>
@import '../../plugins/variable.scss';

.device-check {
	flex: 1 1 auto;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 1rem;
}

.device-check-card {
	max-width: 420px;
	width: 100%;
}

.preview-wrap {
	position: relative;
	background: $body-bg;
	border-radius: 8px;
	overflow: hidden;
	min-height: 220px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.preview-video {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
	transform: scaleX(-1);

	&.preview-video-off { visibility: hidden; }
}

.preview-off-clickable { cursor: pointer; }

.preview-off {
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 6px;
	background: $body-bg;
}

.mic-bar-wrap {
	position: absolute;
	bottom: 8px;
	left: 8px;
	right: 8px;
	display: flex;
	align-items: center;
	gap: 6px;
	z-index: 3;
}

.mic-icon {
	color: $text-muted;
	font-size: .85rem;
	flex-shrink: 0;
}

.mic-bar {
	flex: 1;
	height: 6px;
	background: rgba($white, .15);
	border-radius: 3px;
	overflow: hidden;
}

.mic-fill {
	height: 100%;
	background: $green;
	border-radius: 3px;
	transition: width .08s linear;
}

.ctrl-row {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	background: $dark;
	padding: 6px 10px;
	border-radius: 6px;
	margin-top: 1em;
}
</style>
